import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Applies an API-mapping result (BAG lookup, KvK lookup, ...) onto the ROOT Formly form,
 * not just the field that triggered the lookup.
 *
 * These lookups patch values that can live on a DIFFERENT page than the one the user is
 * currently editing (e.g. the `meta-data` page for BAG fields). `DetailsService.performSave`
 * only submits the active page's model by default (see form-builder `pages` type), so a plain
 * `formControl.patchValue(...)` on the root form updates what's visible on screen but leaves
 * those other pages' controls PRISTINE — the save payload never picks them up and the values
 * are silently lost (work item #20789).
 *
 * Patching WITH events (the default) lets Formly's own valueChanges subscription sync
 * control -> model as usual. Marking the top-level page controls dirty afterwards is what
 * makes `DetailsService.performSave` notice and include them.
 */
@Injectable({
  providedIn: 'root',
})
export class EntryPatchService {
  /**
   * @param rootField The ROOT Formly field (e.g. `UtilityService.getFormOfRootForm(this.field)`),
   *   never the field that triggered the lookup.
   * @param entryPatch A partial entry keyed by top-level page/section keys, e.g.
   *   `{ 'meta-data': { 'bag-is-residential': true } }`.
   */
  applyEntryPatchToRootForm(rootField: FormlyFieldConfig, entryPatch: Record<string, unknown>): void {
    const rootControl = rootField?.formControl;
    if (!rootControl || !entryPatch) return;

    rootControl.patchValue(entryPatch);

    // Angular's patchValue already silently ignores keys with no matching control — mirror
    // that here so an unexpected/renamed top-level key never throws.
    Object.keys(entryPatch).forEach(key => {
      rootControl.get(key)?.markAsDirty();
    });
  }
}
